// Team: POS employees (PIN, admin flag, discount limit, permission grants
// from the server's vocabulary — one aggregate per save) and the store's
// portal access (people added by email, admin or user).

import { useCallback, useEffect, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { api, label, money } from '../../lib/api';
import { useBase } from '../../scope';
import { centsToDollars, Check, dollarsToCents, errMsg, Head, useAction } from './shared';
import type { Employee, EmployeeInput, PortalRole, StoreMembership } from './types-b';

type Draft = {
  employeeId: string | null;
  hasPin: boolean;
  name: string;
  /** '' = keep the current PIN (edit) / no PIN (create) */
  pin: string;
  isActive: boolean;
  isAdmin: boolean;
  discountLimit: string;
  permissions: string[];
};

const freshDraft = (): Draft => ({ employeeId: null, hasPin: false, name: '', pin: '', isActive: true, isAdmin: false, discountLimit: '', permissions: [] });

const toDraft = (e: Employee): Draft => ({
  employeeId: e.employeeId, hasPin: e.hasPin, name: e.name, pin: '', isActive: e.isActive, isAdmin: e.isAdmin,
  discountLimit: centsToDollars(e.discountLimitInCents), permissions: [...e.permissions],
});

const toBody = (d: Draft): EmployeeInput => ({
  name: d.name.trim(),
  ...(d.pin.trim() ? { pin: d.pin.trim() } : {}),
  isActive: d.isActive,
  isAdmin: d.isAdmin,
  discountLimitInCents: dollarsToCents(d.discountLimit),
  permissions: d.permissions,
});

export function Team() {
  const base = useBase();
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [vocab, setVocab] = useState<string[]>([]);
  const [members, setMembers] = useState<StoreMembership[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const empAct = useAction();
  const userAct = useAction();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PortalRole>('user');

  const loadEmployees = useCallback(async () => setEmployees(await api.get<Employee[]>(`${base}/employees`)), [base]);
  const loadMembers = useCallback(async () => setMembers(await api.get<StoreMembership[]>(`${base}/users`)), [base]);

  useEffect(() => {
    void (async () => {
      try {
        const [, perms] = await Promise.all([loadEmployees(), api.get<string[]>(`${base}/permissions`), loadMembers()]);
        setVocab(perms);
        setLoadErr(null);
      } catch (e) {
        setLoadErr(errMsg(e));
      }
    })();
  }, [base, loadEmployees, loadMembers]);

  const empRun = (fn: () => Promise<unknown>) => void empAct.run(async () => { await fn(); await loadEmployees(); });
  const userRun = (fn: () => Promise<unknown>) => void userAct.run(async () => { await fn(); await loadMembers(); });

  const togglePerm = (p: string, on: boolean) =>
    draft && setDraft({ ...draft, permissions: on ? [...draft.permissions.filter((x) => x !== p), p] : draft.permissions.filter((x) => x !== p) });

  return (
    <>
      <Head title="Team" />
      {loadErr && <div className="err" role="alert">{loadErr}</div>}
      <div className="set-cols">
        <div className="panel" data-section="employees">
          <h2>POS employees</h2>
          <div className="hint" style={{ margin: '6px 0 8px' }}>Staff sign in at a terminal with their PIN; what they may do there is their permissions (admins may do everything).</div>
          {employees === null && <div className="loading">Loading…</div>}
          {employees?.length === 0 && <div className="loading">No employees yet.</div>}
          {employees?.map((e) => (
            <div key={e.employeeId} className="rowline" data-employee={e.name}>
              <div className="grow">
                <b style={e.isActive ? undefined : { color: 'var(--ink-3)' }}>{e.name}</b>
                <span className="sub">
                  {e.isAdmin ? 'admin' : `${e.permissions.length} permission${e.permissions.length === 1 ? '' : 's'}`}
                  {' · '}{e.hasPin ? 'PIN set' : 'no PIN'}
                  {e.discountLimitInCents !== null ? ` · discounts to ${money(e.discountLimitInCents)}` : ''}
                  {e.isActive ? '' : ' · inactive'}
                </span>
              </div>
              <button className="btn btn-ghost btn-sm" title={`Edit ${e.name}`} disabled={empAct.busy} onClick={() => setDraft(toDraft(e))}><Pencil size={14} /></button>
              <button className="btn btn-ghost btn-sm" title={`Delete ${e.name}`} disabled={empAct.busy}
                onClick={() => { if (confirm(`Delete the employee “${e.name}”?`)) empRun(async () => { await api.del(`${base}/employees/${e.employeeId}`); setDraft((d) => (d?.employeeId === e.employeeId ? null : d)); }); }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {!draft && (
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-outline" onClick={() => setDraft(freshDraft())}>New employee</button>
            </div>
          )}
          {draft && (
            <div className="set-stack" style={{ marginTop: 14, gap: 12 }} data-section="employee-form">
              <div className="overline">{draft.employeeId ? `Edit ${draft.name}` : 'New employee'}</div>
              <div className="frow">
                <div className="field">
                  <label htmlFor="emp-name">Name</label>
                  <input id="emp-name" className="input" aria-label="Employee name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </div>
                <div className="field narrow" style={{ flexBasis: 130 }}>
                  <label htmlFor="emp-pin">PIN</label>
                  <input id="emp-pin" className="input" inputMode="numeric" aria-label="PIN" placeholder={draft.employeeId && draft.hasPin ? 'unchanged' : '4–8 digits'}
                    value={draft.pin} onChange={(e) => setDraft({ ...draft, pin: e.target.value.replace(/\D/g, '') })} />
                </div>
                <div className="field narrow" style={{ flexBasis: 140 }}>
                  <label htmlFor="emp-discount">Discount limit ($)</label>
                  <input id="emp-discount" className="input" inputMode="decimal" title="Discount limit" placeholder="no limit"
                    value={draft.discountLimit} onChange={(e) => setDraft({ ...draft, discountLimit: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18 }}>
                <Check label="Active" checked={draft.isActive} onChange={(v) => setDraft({ ...draft, isActive: v })} />
                <Check label="Admin (every permission)" checked={draft.isAdmin} onChange={(v) => setDraft({ ...draft, isAdmin: v })} />
              </div>
              <div data-section="permissions">
                <div className="overline" style={{ marginBottom: 4 }}>Permissions</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', columnGap: 14 }}>
                  {vocab.map((p) => (
                    <label key={p} className="checkline">
                      <input type="checkbox" aria-label={p} checked={draft.isAdmin || draft.permissions.includes(p)} disabled={draft.isAdmin}
                        onChange={(e) => togglePerm(p, e.target.checked)} />
                      {label(p)}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-primary" disabled={empAct.busy || !draft.name.trim()}
                  onClick={() => empRun(async () => {
                    if (draft.employeeId) await api.put<Employee>(`${base}/employees/${draft.employeeId}`, toBody(draft));
                    else await api.post<Employee>(`${base}/employees`, toBody(draft));
                    setDraft(null);
                  })}>
                  {empAct.busy ? 'Saving…' : draft.employeeId ? 'Save employee' : 'Create employee'}
                </button>
                <button className="btn btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
              </div>
            </div>
          )}
          {empAct.error && <div className="err" role="alert">{empAct.error}</div>}
        </div>

        <div className="panel" data-section="store-users">
          <h2>Portal access</h2>
          <div className="hint" style={{ margin: '6px 0 8px' }}>
            People who can sign in to this admin portal for this store. Admins manage settings and people; users run the day. A new email gets a sign-in link.
          </div>
          {members === null && <div className="loading">Loading…</div>}
          {members?.length === 0 && <div className="loading">Nobody has store-level access yet (restaurant members see every store).</div>}
          {members?.map((m) => (
            <div key={m.userId} className="rowline" data-member={m.email}>
              <div className="grow">
                <b>{m.displayName ?? m.email}</b>
                <span className="sub">{m.displayName ? `${m.email} · ` : ''}since {new Date(m.createdAt).toLocaleDateString('en-AU')}</span>
              </div>
              <select className="input" style={{ width: 110 }} aria-label={`Role for ${m.email}`} value={m.role} disabled={userAct.busy}
                onChange={(e) => userRun(() => api.put(`${base}/users/${m.userId}`, { role: e.target.value }))}>
                <option value="admin">admin</option>
                <option value="user">user</option>
              </select>
              <button className="btn btn-ghost btn-sm" title={`Remove ${m.email}`} disabled={userAct.busy}
                onClick={() => { if (confirm(`Remove ${m.email}'s access to this store?`)) userRun(() => api.del(`${base}/users/${m.userId}`)); }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="frow" style={{ marginTop: 12 }}>
            <div className="field">
              <label htmlFor="member-email">Email</label>
              <input id="member-email" className="input" type="email" placeholder="manager@example.com" aria-label="Member email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field narrow" style={{ flexBasis: 110 }}>
              <label htmlFor="member-role">Role</label>
              <select id="member-role" className="input" aria-label="New member role" value={role} onChange={(e) => setRole(e.target.value as PortalRole)}>
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </div>
            <button className="btn btn-outline" disabled={userAct.busy || !email.trim()}
              onClick={() => userRun(async () => { await api.post<StoreMembership>(`${base}/users`, { email: email.trim(), role }); setEmail(''); })}>
              Grant access
            </button>
          </div>
          {userAct.error && <div className="err" role="alert">{userAct.error}</div>}
        </div>
      </div>
    </>
  );
}
